// apps/client/src/pages/offices/OfficeManagementPage.tsx (RESTORED TO WORKING STATE)

import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { collection, addDoc, Timestamp, setDoc, doc, deleteDoc, query, onSnapshot, getDocs } from 'firebase/firestore';
import Modal from '../../components/common/Modal';
import TransferBrokerModal from '../../components/offices/TransferBrokerModal';
import { Plus, Pencil, Trash2, Building2, BarChart3 } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { getBrokerDisplayName } from '../../utils/dataProcessing';

// --- Type Definitions ---
// Broker interface removed - not used in this component

interface Office {
  id: string;
  name: string;
  location?: string;
  description?: string;
  assignedBrokers?: Array<{ name: string; status: string }>;
}

interface OutletContextType {
  userData: {
    companyId: string;
    role: string;
  };
  offices: Office[]; // All offices for "Assigned Elsewhere" logic
  userVisibleOffices: Office[];
}

const OfficeManagementPage: React.FC = () => {
  const { userData, offices: allOffices, userVisibleOffices } = useOutletContext<OutletContextType>();
  const navigate = useNavigate();

  // State for brokers from commission_data
  const [brokers, setBrokers] = useState<string[]>([]);
  const [loadingBrokers, setLoadingBrokers] = useState(true);
  const [brokerOfficeMap, setBrokerOfficeMap] = useState<Record<string, Array<{ name: string; status: string }>>>({});
  const [inactiveBrokerOfficeMap, setInactiveBrokerOfficeMap] = useState<Record<string, Array<{ name: string; status: string; transferDate?: string }>>>({});

  // UI and Form State
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [brokerStatuses, setBrokerStatuses] = useState<Record<string, string>>({});
  const [showInactiveBrokers, setShowInactiveBrokers] = useState(false);
  
  const [showCreateOffice, setShowCreateOffice] = useState(false);
  const [showEditOffice, setShowEditOffice] = useState(false);
  const [editingOffice, setEditingOffice] = useState<Office | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [officeToDelete, setOfficeToDelete] = useState<Office | null>(null);
  const [officeName, setOfficeName] = useState('');
  const [officeLocation, setOfficeLocation] = useState('');
  const [officeDescription, setOfficeDescription] = useState('');
  const [selectedBrokers, setSelectedBrokers] = useState<string[]>([]); // Stores broker names
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Drag-and-drop and transfer state
  const [draggedBroker, setDraggedBroker] = useState<{ id: string; name: string; sourceOfficeId: string } | null>(null);
  const [dropTargetOfficeId, setDropTargetOfficeId] = useState<string | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState<{
    broker: { id: string; name: string } | null;
    sourceOffice: { id: string; name: string } | null;
    targetOffice: { id: string; name: string } | null;
  }>({
    broker: null,
    sourceOffice: null,
    targetOffice: null
  });

  // Map broker names to broker IDs
  const [brokerIdMap, setBrokerIdMap] = useState<Record<string, string>>({});

  useEffect(() => {
  if (!userData?.companyId) {
    setLoadingBrokers(false);
    return;
  }

  const brokersQuery = query(collection(db, `companies/${userData.companyId}/brokers`));
  const brokersUnsubscribe = onSnapshot(brokersQuery, (snapshot: any) => {
    const allBrokerData: Array<{
      id: string;
      name: string;
      currentOfficeId: string | null;
      allOfficeIds: string[];
      status: string;
      isAssigned: boolean;
      officeHistory?: Array<{
        officeId: string;
        officeName: string;
        effectiveFrom: { toDate: () => Date };
        effectiveTo: { toDate: () => Date } | null;
      }>;
    }> = [];
    const idMap: Record<string, string> = {};

    snapshot.docs.forEach((doc: any) => {
      const data = doc.data();
      // ✅ FIX: Skip merged brokers - they should be hidden from all views
      if (data.mergedInto) {
        return; // This broker was merged into another, don't show it
      }
      if (data.currentName && typeof data.currentName === 'string') {
        allBrokerData.push({
          id: doc.id,
          name: data.currentName,
          currentOfficeId: data.currentOfficeId || null,
          allOfficeIds: data.allOfficeIds || [],
          status: data.status || 'pending',
          isAssigned: !!data.currentOfficeId,
          officeHistory: data.officeHistory || []
        });
        idMap[data.currentName] = doc.id;
      }
    });

    // Store broker names for form selection
    setBrokers(allBrokerData.map((b: any) => b.name).sort());
    setBrokerIdMap(idMap);

    // Build office assignments from broker data - ONLY current office assignment
    const officeAssignments: Record<string, Array<{ name: string; status: string }>> = {};
    const inactiveOfficeAssignments: Record<string, Array<{ name: string; status: string; transferDate?: string }>> = {};

    allBrokerData.forEach(broker => {
      // ✅ CURRENT BROKERS: Only show broker in their CURRENT office
      if (broker.currentOfficeId) {
        if (!officeAssignments[broker.currentOfficeId]) {
          officeAssignments[broker.currentOfficeId] = [];
        }
        officeAssignments[broker.currentOfficeId].push({ name: broker.name, status: broker.status });
      }

      // ✅ INACTIVE BROKERS: Show brokers who have transferred OUT of each office
      if (broker.allOfficeIds && broker.allOfficeIds.length > 0) {
        broker.allOfficeIds.forEach(officeId => {
          // Only include if this was a PREVIOUS office (not current)
          if (officeId !== broker.currentOfficeId) {
            if (!inactiveOfficeAssignments[officeId]) {
              inactiveOfficeAssignments[officeId] = [];
            }

            // Find the transfer date from office history
            let transferDate: string | undefined;
            if (broker.officeHistory && broker.officeHistory.length > 0) {
              const historyEntry = broker.officeHistory.find(
                h => h.officeId === officeId && h.effectiveTo !== null
              );
              if (historyEntry && historyEntry.effectiveTo) {
                transferDate = historyEntry.effectiveTo.toDate().toLocaleDateString();
              }
            }

            inactiveOfficeAssignments[officeId].push({
              name: broker.name,
              status: broker.status,
              transferDate
            });
          }
        });
      }
    });

  // console.log('Broker office assignments:', officeAssignments);
  // console.log('Inactive broker office assignments:', inactiveOfficeAssignments);
    setBrokerOfficeMap(officeAssignments);
    setInactiveBrokerOfficeMap(inactiveOfficeAssignments);
    setLoadingBrokers(false);
  }, (err: any) => {
  // console.error('Error loading brokers:', err);
    setError('Could not load the broker list.');
    setLoadingBrokers(false);
  });

  return () => {
    brokersUnsubscribe();
  };
}, [userData]);

  // Helper functions
  const getUnassignedBrokers = () => {
    const assignedBrokerNames = new Set<string>();
    Object.values(brokerOfficeMap).forEach(brokerList => {
      brokerList.forEach(broker => assignedBrokerNames.add(broker.name));
    });
    return brokers.filter((name: any) => !assignedBrokerNames.has(name));
  };

  const handleToggleBrokerStatus = (brokerName: string) => {
    setBrokerStatuses(prev => ({ 
      ...prev, 
      [brokerName]: (prev[brokerName] || 'active') === 'active' ? 'inactive' : 'active' 
    }));
  };

  const toggleBrokerSelection = (brokerName: string) => { 
    setSelectedBrokers(prev => 
      prev.includes(brokerName) 
        ? prev.filter((name: any) => name !== brokerName) 
        : [...prev, brokerName]
    ); 
  };

  const resetForm = () => {
    setOfficeName('');
    setOfficeLocation('');
    setOfficeDescription('');
    setSelectedBrokers([]);
    setEditingOffice(null);
    setError('');
    setBrokerStatuses({});
  };

  // ============================================
  // DRAG-AND-DROP HANDLERS (Google Enterprise Pattern)
  // ============================================
  const handleDragStart = (brokerName: string, sourceOfficeId: string) => {
    const brokerId = brokerIdMap[brokerName];
    if (brokerId) {
      setDraggedBroker({ id: brokerId, name: brokerName, sourceOfficeId });
    }
  };

  const handleDragOver = (e: React.DragEvent, officeId: string) => {
    e.preventDefault(); // Allow drop
    setDropTargetOfficeId(officeId);
  };

  const handleDragLeave = () => {
    setDropTargetOfficeId(null);
  };

  const handleDrop = (e: React.DragEvent, targetOfficeId: string, targetOfficeName: string) => {
    e.preventDefault();
    setDropTargetOfficeId(null);

    if (!draggedBroker) return;

    // Prevent dropping on same office
    if (draggedBroker.sourceOfficeId === targetOfficeId) {
      setDraggedBroker(null);
      return;
    }

    // Find source office name
    const sourceOffice = userVisibleOffices.find(o => o.id === draggedBroker.sourceOfficeId);

    // Open transfer modal
    setTransferData({
      broker: { id: draggedBroker.id, name: draggedBroker.name },
      sourceOffice: sourceOffice ? { id: sourceOffice.id, name: sourceOffice.name } : null,
      targetOffice: { id: targetOfficeId, name: targetOfficeName }
    });
    setShowTransferModal(true);
    setDraggedBroker(null);
  };

  // ============================================
  // TRANSFER BUTTON HANDLER
  // ============================================
  const handleTransferButtonClick = (brokerName: string, sourceOfficeId: string) => {
    const brokerId = brokerIdMap[brokerName];
    if (!brokerId) return;

    const sourceOffice = userVisibleOffices.find(o => o.id === sourceOfficeId);

    // Open transfer modal without pre-selected target
    setTransferData({
      broker: { id: brokerId, name: brokerName },
      sourceOffice: sourceOffice ? { id: sourceOffice.id, name: sourceOffice.name } : null,
      targetOffice: null // User will select manually
    });
    setShowTransferModal(true);
  };

  // ============================================
  // TRANSFER COMPLETION HANDLER
  // ============================================
  const handleTransferComplete = (success: boolean, message: string) => {
    if (success) {
      setSuccess(message);
      setError('');
      // Brokers will auto-refresh via onSnapshot
    } else {
      setError(message);
      setSuccess('');
    }

    // Auto-clear messages after 5 seconds
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 5000);
  };

  const handleCreateOffice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!officeName.trim()) {
      setError('Office name is required');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Create office document
      const officeData = {
        name: officeName.trim(),
        location: officeLocation.trim(),
        description: officeDescription.trim(),
        isHeadOffice: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        status: 'active',
        brokerIds: [],
        brokerCount: selectedBrokers.length
      };

      const officeRef = await addDoc(collection(db, `companies/${userData.companyId}/offices`), officeData);

      // Get all brokers
      const brokersQuery = query(collection(db, `companies/${userData.companyId}/brokers`));
      const brokersSnapshot = await getDocs(brokersQuery);

      // Assign selected brokers to this office ONLY (single office assignment enforced)
      const updatePromises = brokersSnapshot.docs
        .filter((doc: any) => selectedBrokers.includes(doc.data().currentName))
        .map((doc: any) =>
          setDoc(doc.ref, {
            allOfficeIds: [officeRef.id],  // Replace with single office
            currentOfficeId: officeRef.id,
            status: brokerStatuses[doc.data().currentName] || 'active',
            updatedAt: Timestamp.now()
          }, { merge: true })
        );

      await Promise.all(updatePromises);

      setSuccess('Office created successfully!');
      setShowCreateOffice(false);
      resetForm();
    } catch (err: any) {
  // console.error("Error creating office:", err);
      setError(`Failed to create office: ${err.message}.`);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOffice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOffice || !officeName.trim()) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const officeRef = doc(db, `companies/${userData.companyId}/offices`, editingOffice.id);
      
      // Update office document
      await setDoc(officeRef, {
        name: officeName.trim(),
        location: officeLocation.trim(),
        description: officeDescription.trim(),
        brokerCount: selectedBrokers.length,
        updatedAt: Timestamp.now(),
      }, { merge: true });
      
      // Get all brokers
      const brokersQuery = query(collection(db, `companies/${userData.companyId}/brokers`));
      const brokersSnapshot = await getDocs(brokersQuery);
      
      // Unassign brokers that were removed
      const unassignPromises = brokersSnapshot.docs
        .filter((doc: any) => {
          const data = doc.data();
          return data.allOfficeIds?.includes(editingOffice.id) && !selectedBrokers.includes(data.currentName);
        })
        .map((doc: any) => {
          const data = doc.data();
          const updatedOfficeIds = (data.allOfficeIds || []).filter((id: string) => id !== editingOffice.id);
          return setDoc(doc.ref, {
            allOfficeIds: updatedOfficeIds,
            currentOfficeId: updatedOfficeIds.length > 0 ? updatedOfficeIds[0] : null,
            status: updatedOfficeIds.length > 0 ? (data.status || 'active') : 'pending',
            updatedAt: Timestamp.now()
          }, { merge: true });
        });
      
      // Assign newly selected brokers (single office assignment enforced)
      const assignPromises = brokersSnapshot.docs
        .filter((doc: any) => selectedBrokers.includes(doc.data().currentName))
        .map((doc: any) => {
          const data = doc.data();

          return setDoc(doc.ref, {
            allOfficeIds: [editingOffice.id],  // Replace with single office (transfer if changing)
            currentOfficeId: editingOffice.id,
            status: brokerStatuses[data.currentName] || 'active',
            updatedAt: Timestamp.now()
          }, { merge: true });
        });

      await Promise.all([...unassignPromises, ...assignPromises]);
      
      setSuccess('Office updated successfully!');
      setShowEditOffice(false);
      resetForm();
    } catch (err: any) {
  // console.error("Error updating office:", err);
      setError(`Failed to update office: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOffice = async () => {
    if (!officeToDelete) return;
    setDeleting(true);
    
    try {
      await deleteDoc(doc(db, `companies/${userData.companyId}/offices`, officeToDelete.id));
      setSuccess(`Office "${officeToDelete.name}" deleted successfully!`);
      setShowDeleteConfirm(false);
      setOfficeToDelete(null);
    } catch (err: any) {
      setError(`Failed to delete office: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (office: Office) => {
    setEditingOffice(office);
    setOfficeName(office.name || '');
    setOfficeLocation(office.location || '');
    setOfficeDescription(office.description || '');
    
    const initialStatuses: Record<string, string> = {};
    const brokerNames: string[] = [];
    
    // ✅ CORRECT: Read from brokerOfficeMap
    const officeBrokers = brokerOfficeMap[office.id] || [];
    officeBrokers.forEach(broker => {
        initialStatuses[broker.name] = broker.status || 'active';
        brokerNames.push(broker.name);

      });
    
    setSelectedBrokers(brokerNames);
    setBrokerStatuses(initialStatuses);
    setShowEditOffice(true);
    setError('');
  };

  const renderModalForm = (handleSubmit: (e: React.FormEvent) => Promise<void>, isEditing: boolean) => (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">Office Name *</label>
        <input 
          type="text" 
          value={officeName} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOfficeName(e.target.value)} 
          className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2" 
          required 
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
        <input 
          type="text" 
          value={officeLocation} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOfficeLocation(e.target.value)} 
          className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2" 
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
        <textarea 
          value={officeDescription} 
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setOfficeDescription(e.target.value)} 
          className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2" 
          rows={3}
        />
      </div>
      
      <div className="mb-6">
  <label className="block text-sm font-medium text-gray-300 mb-2">Assign Brokers</label>
  <div className="bg-gray-700 rounded-md p-4 max-h-48 overflow-y-auto">
    {brokers.length > 0 ? (
      <div className="space-y-2">
        {/* Select All/Unselect All Checkbox */}
        <div className="flex items-center justify-between p-2 rounded bg-gray-600 border-b border-gray-500 mb-2">
          <label className="flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={
                brokers.filter((name: any) => 
                  !allOffices.some(o => 
                    o.id !== editingOffice?.id && 
                    o.assignedBrokers?.some(b => b.name === name)
                  )
                ).every(name => selectedBrokers.includes(name)) &&
                brokers.filter((name: any) => 
                  !allOffices.some(o => 
                    o.id !== editingOffice?.id && 
                    o.assignedBrokers?.some(b => b.name === name)
                  )
                ).length > 0
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const availableBrokers = brokers.filter((name: any) => 
                  !allOffices.some(o => 
                    o.id !== editingOffice?.id && 
                    o.assignedBrokers?.some(b => b.name === name)
                  )
                );
                
                if (e.target.checked) {
                  // Select all available brokers
                  setSelectedBrokers(prev => 
                    [...new Set([...prev, ...availableBrokers])]
                  );
                } else {
                  // Unselect all
                  setSelectedBrokers([]);
                }
              }}
              className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded" 
            />
            <span className="ml-3 text-sm text-white font-medium">Select All / Unselect All</span>
          </label>
        </div>
        
        {/* Individual Broker Checkboxes */}
        {brokers.map((brokerName: any) => {
          const isSelected = selectedBrokers.includes(brokerName);
          const isInAnotherOffice = !isSelected && (() => {
            // Check if broker is assigned to another office using brokerOfficeMap
            for (const [officeId, brokerList] of Object.entries(brokerOfficeMap)) {
              if (officeId !== editingOffice?.id && brokerList.some(b => b.name === brokerName)) {
                return true;
              }
            }
            return false;
          })();
          
          return (
            <div 
              key={brokerName} 
              className={`flex items-center justify-between p-2 rounded ${
                isInAnotherOffice ? 'opacity-50' : 'hover:bg-gray-600'
              }`}
            >
              <label className={`flex items-center flex-grow ${
                isInAnotherOffice ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}>
                <input 
                  type="checkbox" 
                  checked={isSelected} 
                  onChange={() => !isInAnotherOffice && toggleBrokerSelection(brokerName)} 
                  disabled={isInAnotherOffice} 
                  className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded" 
                />
                <span className="ml-3 text-sm text-white">{getBrokerDisplayName(brokerName)}</span>
                {isInAnotherOffice && (
                  <span className="ml-auto text-xs text-yellow-400 mr-2">
                    Assigned Elsewhere
                  </span>
                )}
              </label>
              {isEditing && isSelected && (
                <button 
                  type="button" 
                  onClick={() => handleToggleBrokerStatus(brokerName)} 
                  className={`px-2 py-0.5 text-xs rounded-full font-semibold ${
                    (brokerStatuses[brokerName] || 'active') === 'active' 
                      ? 'bg-green-200 text-green-800' 
                      : 'bg-gray-500 text-gray-100'
                  }`}
                >
                  {brokerStatuses[brokerName] || 'active'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <p className="text-sm text-gray-400">No brokers found in commission data.</p>
    )}
  </div>
</div>
      
      <div className="flex gap-3 justify-end">
        <button 
          type="button" 
          onClick={() => { 
            setShowCreateOffice(false); 
            setShowEditOffice(false); 
            resetForm(); 
          }} 
          className="px-4 py-2 bg-gray-600 text-white rounded-md"
        >
          Cancel
        </button>
        <button 
          type="submit" 
          disabled={saving} 
          className="px-4 py-2 bg-blue-600 text-white rounded-md"
        >
          {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Office')}
        </button>
      </div>
    </form>
  );

  // --- Render ---
  return (
    <div className="space-y-6">
      <PageHeader
        icon={Building2}
        title="Office Management"
        subtitle="Manage your company offices and assign brokers"
        actions={
          <div className="flex gap-3">
            <button onClick={() => navigate('/offices/compare')} className="px-4 py-2 bg-gray-700 text-white rounded-md flex items-center gap-2 hover:bg-gray-600">
              <BarChart3 className="w-5 h-5" />
              Compare Offices
            </button>
            <button onClick={() => { resetForm(); setShowCreateOffice(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center gap-2">
              <Plus className="w-5 h-5" /> Create Office
            </button>
          </div>
        }
      />

      {error && <div className="bg-red-900 p-3 rounded mb-4">{error}</div>}
      {success && <div className="bg-green-900 p-3 rounded mb-4">{success}</div>}

      {['super_admin', 'office_admin', 'administrator'].includes(userData?.role) &&
       getUnassignedBrokers().length > 0 && (
        <div className="bg-yellow-900 text-yellow-300 p-4 rounded mb-6">
        <p>Unassigned Broker(s): {getUnassignedBrokers().map((name: any) => getBrokerDisplayName(name)).join(', ')}</p>
        </div>
      )}

      {/* Toggle for showing inactive brokers - RED styling for visibility */}
      <div className="flex items-center gap-2 mb-4">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={showInactiveBrokers}
            onChange={(e) => setShowInactiveBrokers(e.target.checked)}
            className="w-4 h-4 text-red-500 bg-gray-700 border-red-500 rounded focus:ring-red-500 focus:ring-2 accent-red-500"
          />
          <span className={`ml-2 text-sm ${showInactiveBrokers ? 'text-red-400 font-medium' : 'text-gray-400'}`}>
            Show inactive brokers (transferred out)
          </span>
        </label>
      </div>

      {userVisibleOffices.length === 0 && !loadingBrokers ? (
        <div className="bg-gray-800 p-12 rounded-lg text-center">
          <h3 className="text-xl font-semibold">No Offices To Display</h3>
          <p className="text-gray-400 mt-2">
            {['super_admin', 'administrator', 'office_admin'].includes(userData?.role) 
              ? 'Create an office or check your assignments.' 
              : 'No offices found.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {userVisibleOffices.map((office: any) => {
            // Get active (current) brokers
            const validBrokers = (brokerOfficeMap[office.id] || []).filter((b: any) => b && b.name);
            const sortedBrokers = [...validBrokers].sort((a: any, b: any) => {
              const nameA = a.name || '';
              const nameB = b.name || '';
              const statusA = a.status || 'active';
              const statusB = b.status || 'active';
              if (statusA === 'active' && statusB !== 'active') return -1;
              if (statusA !== 'active' && statusB === 'active') return 1;
              return nameA.localeCompare(nameB);
            });

            // Get inactive (transferred out) brokers if toggle is enabled
            const inactiveBrokers = showInactiveBrokers
              ? (inactiveBrokerOfficeMap[office.id] || []).filter((b: any) => b && b.name)
              : [];
            const sortedInactiveBrokers = [...inactiveBrokers].sort((a: any, b: any) => {
              const nameA = a.name || '';
              const nameB = b.name || '';
              return nameA.localeCompare(nameB);
            });

            const isDropTarget = dropTargetOfficeId === office.id;

            return (
              <div
                key={office.id}
                className={`bg-gray-800 rounded-lg shadow-lg p-6 flex flex-col transition-all ${
                  isDropTarget ? 'ring-4 ring-blue-500 bg-gray-700' : ''
                }`}
                onDragOver={(e) => handleDragOver(e, office.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, office.id, office.name)}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-white">{office.name}</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(office)}
                      className="text-gray-400 hover:text-blue-400"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setOfficeToDelete(office);
                        setShowDeleteConfirm(true);
                      }}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <h4 className="text-sm font-medium text-gray-400">
                  Assigned Brokers ({sortedBrokers.length || 0})
                  {showInactiveBrokers && sortedInactiveBrokers.length > 0 && (
                    <span className="text-red-400"> + {sortedInactiveBrokers.length} inactive</span>
                  )}
                </h4>

                <div className="border-b border-gray-700 my-2"></div>

                <div className="flex-grow">
                  {sortedBrokers.length > 0 || sortedInactiveBrokers.length > 0 ? (
                    <div className="space-y-2">
                      {/* Active brokers */}
                      {sortedBrokers.map((broker, index) => (
                        <div
                          key={`${office.id}-${broker.name}-${index}`}
                          draggable
                          onDragStart={() => handleDragStart(broker.name, office.id)}
                          className="flex items-center justify-between text-sm text-gray-300 p-2 rounded cursor-move hover:bg-gray-700 transition-colors group"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {/* Drag handle icon */}
                            <svg className="w-4 h-4 text-gray-500 flex-shrink-0 group-hover:text-emerald-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                            </svg>

                            <span className="truncate" title={broker.name}>
                              {getBrokerDisplayName(broker.name)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Transfer button - ALWAYS VISIBLE for better UX */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTransferButtonClick(broker.name, office.id);
                              }}
                              className="p-1 text-gray-400 rounded transition-colors"
                              title="Transfer to another office"
                            >
                              <svg className="w-4 h-4 group-hover:text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                            </button>

                            {/* Status badge */}
                            <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${
                              (broker.status || 'active') === 'active'
                                ? 'bg-green-200 text-green-800'
                                : 'bg-gray-600 text-gray-400'
                            }`}>
                              {broker.status || 'active'}
                            </span>
                          </div>
                        </div>
                      ))}

                      {/* Inactive brokers (transferred out) */}
                      {showInactiveBrokers && sortedInactiveBrokers.length > 0 && (
                        <>
                          {sortedBrokers.length > 0 && (
                            <div className="border-t border-red-800/50 my-3 pt-3">
                              <p className="text-xs text-red-400 font-medium mb-2">Transferred Out</p>
                            </div>
                          )}
                          {sortedInactiveBrokers.map((broker, index) => (
                            <div
                              key={`${office.id}-inactive-${broker.name}-${index}`}
                              className="flex items-center justify-between text-sm text-gray-500 p-2 rounded bg-gray-800/50"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {/* Non-draggable icon */}
                                <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                                </svg>

                                <span className="truncate" title={broker.name}>
                                  {getBrokerDisplayName(broker.name)}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 flex-shrink-0">
                                {/* Transfer date badge */}
                                {broker.transferDate && (
                                  <span className="text-xs text-red-400/70">
                                    {broker.transferDate}
                                  </span>
                                )}

                                {/* Inactive badge - RED for visibility */}
                                <span className="px-2 py-0.5 text-xs rounded-full font-semibold bg-red-900/50 text-red-400 border border-red-700/50">
                                  inactive
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No brokers assigned.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showCreateOffice && (
        <Modal 
          title="Create New Office" 
          show={showCreateOffice} 
          onClose={() => setShowCreateOffice(false)}
        >
          {renderModalForm(handleCreateOffice, false)}
        </Modal>
      )}

      {showEditOffice && (
        <Modal 
          title={`Edit Office: ${editingOffice?.name}`} 
          show={showEditOffice} 
          onClose={() => setShowEditOffice(false)}
        >
          {renderModalForm(handleUpdateOffice, true)}
        </Modal>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete the office "{officeToDelete?.name}"?
            </p>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setShowDeleteConfirm(false)} 
                className="px-4 py-2 bg-gray-600 text-white rounded-md"
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteOffice} 
                disabled={deleting} 
                className="px-4 py-2 bg-red-600 text-white rounded-md"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Broker Modal */}
      <TransferBrokerModal
        show={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        broker={transferData.broker}
        sourceOffice={transferData.sourceOffice}
        targetOffice={transferData.targetOffice}
        onTransferComplete={handleTransferComplete}
        availableOffices={userVisibleOffices.map(o => ({ id: o.id, name: o.name }))}
      />
    </div>
  );
};

export default OfficeManagementPage;
